import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, LogOut, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/brand";

const COLLECTION_LINKS = [
  { label: "Bomboniere", slug: "inizio-del-cammino" },
  { label: "Stagioni", slug: "il-ritmo-della-terra" },
  { label: "Segni zodiacali", slug: "la-bussola-del-cielo" },
  { label: "Cicli lunari", slug: "il-respiro-della-luna" },
  { label: "Chakra", slug: "le-sette-ruote" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [collMenu, setCollMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-[hsl(40_33%_96%/0.75)] backdrop-blur-xl border-b border-white/50" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={LOGO} alt="Incanti d'Estate" className="h-12 w-12 object-contain transition-transform duration-500 group-hover:rotate-6" />
          <span className="font-serif text-2xl md:text-[1.6rem] leading-none tracking-tight text-gold">Incanti d'Estate</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10 text-sm tracking-wide">
          <Link to="/" className="hover:text-gold transition-colors duration-300" data-testid="nav-home">Home</Link>
          <a href="/#manifesto" className="hover:text-gold transition-colors duration-300" data-testid="nav-percorso">Il percorso</a>
          <div
            className="relative"
            onMouseEnter={() => setCollMenu(true)}
            onMouseLeave={() => setCollMenu(false)}
          >
            <Link
              to="/shop"
              className="flex items-center gap-1 hover:text-gold transition-colors duration-300"
              data-testid="nav-shop"
              aria-haspopup="true"
              aria-expanded={collMenu}
            >
              Collezioni
              <ChevronDown size={14} className={`transition-transform duration-300 ${collMenu ? "rotate-180" : ""}`} />
            </Link>
            <AnimatePresence>
              {collMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                  data-testid="collezioni-dropdown"
                >
                  <div className="min-w-[220px] rounded-2xl bg-[hsl(40_33%_96%/0.9)] backdrop-blur-xl border border-white/60 shadow-[0_12px_40px_-12px_rgba(120,100,60,0.35)] p-2">
                    {COLLECTION_LINKS.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/collezione/${c.slug}`}
                        className="block px-4 py-2.5 rounded-xl text-sm text-foreground/80 hover:text-gold hover:bg-white/60 transition-colors duration-200"
                        data-testid={`collezioni-dropdown-${c.slug}`}
                        onClick={() => setCollMenu(false)}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/faq" className="hover:text-gold transition-colors duration-300" data-testid="nav-faq">FAQ</Link>
        </nav>

        <div className="flex items-center gap-4">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm hover:text-gold transition-colors">Admin</Link>
              )}
              <Link to="/ordini" data-testid="nav-orders" className="text-sm hover:text-gold transition-colors">Ordini</Link>
              <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <LogOut size={16} /> Esci
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden md:flex items-center gap-1 text-sm hover:text-gold transition-colors">
              <User size={16} /> Accedi
            </Link>
          )}
          <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative p-2 hover:text-gold transition-colors">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-gold text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{count}</span>
            )}
          </button>
          <button className="md:hidden p-2" onClick={() => setMenu(!menu)} data-testid="mobile-menu-btn">
            {menu ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {menu && (
        <div className="md:hidden bg-[hsl(40_33%_96%/0.95)] backdrop-blur-xl border-t border-white/50 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          <Link to="/" onClick={() => setMenu(false)}>Home</Link>
          <a href="/#manifesto" onClick={() => setMenu(false)}>Il percorso</a>
          <Link to="/shop" onClick={() => setMenu(false)}>Collezioni</Link>
          <div className="pl-4 flex flex-col gap-3 border-l border-gold/30" data-testid="mobile-collezioni-list">
            {COLLECTION_LINKS.map((c) => (
              <Link
                key={c.slug}
                to={`/collezione/${c.slug}`}
                onClick={() => setMenu(false)}
                className="text-sm text-foreground/70 hover:text-gold transition-colors"
                data-testid={`mobile-collezioni-${c.slug}`}
              >
                {c.label}
              </Link>
            ))}
          </div>
          <Link to="/faq" onClick={() => setMenu(false)}>FAQ</Link>
          {user ? (
            <>
              {user.role === "admin" && <Link to="/admin" onClick={() => setMenu(false)}>Admin</Link>}
              <Link to="/ordini" onClick={() => setMenu(false)}>I miei ordini</Link>
              <button className="text-left" onClick={() => { logout(); setMenu(false); nav("/"); }}>Esci</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMenu(false)}>Accedi</Link>
          )}
        </div>
      )}
    </header>
  );
}
